import {
	faArrowLeft,
	faHeadset,
	faPaperPlane,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateTime } from '@supply-mover-2-0/core';
import {
	IssueStatus,
	PORTAL_ISSUE_MESSAGE_MAX_LENGTH,
	type PortalIssueMessageDto,
} from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccountBreadcrumb } from '../components/AccountBreadcrumb';
import { EmptyState } from '../components/EmptyState';
import { IssueStatusChip } from '../components/IssueStatusChip';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { HistoryListSkeleton } from '../components/skeletons';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as portalIssueService from '../services/portalIssueService';
import { useLoginModalStore } from '../stores/loginModalStore';

/**
 * One response on the ticket (PD-15002) — a full-width row rather than a
 * chat bubble: author + timestamp on top, the message below. Support
 * responses carry an accent edge + tag so the two sides stay easy to
 * tell apart without the chat-style alignment.
 */
function ResponseRow({
	message,
}: {
	message: PortalIssueMessageDto;
}): JSX.Element {
	const { t } = useTranslation();
	const isCustomer = message.author === 'customer';

	return (
		<div
			className={`bg-brand-surface border border-brand-border rounded-xl p-3 ${
				isCustomer ? '' : 'border-l-4 border-l-portal-accent'
			}`}
		>
			<div className="flex items-center justify-between gap-2 mb-1">
				<span className="text-xs font-medium text-default flex items-center gap-1.5">
					{isCustomer
						? t('You')
						: message.authorName != null
							? t(message.authorName)
							: t('Support team')}
					{!isCustomer && (
						<span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-portal-accent/10 text-portal-accent">
							{t('Support')}
						</span>
					)}
				</span>
				<span className="text-xs text-brand-muted">
					{formatDateTime(message.createdDate as unknown as string)}
				</span>
			</div>
			<div className="text-sm text-default whitespace-pre-wrap break-words">
				{t(message.body)}
			</div>
		</div>
	);
}

/**
 * Support request detail (PD-13773, reworked in PD-15002). The ticket
 * reads as a card — subject, reference, category, status and the original
 * submission — followed by a flat Responses list and a reply box. Replying
 * to a Resolved request reopens it so the support team sees the new
 * correspondence.
 */
export function SupportIssueDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const params = useParams<{ id: string }>();
	const issueId = params.id != null ? parseInt(params.id, 10) : null;
	const { token, isAuthenticated } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const queryClient = useQueryClient();

	const [reply, setReply] = useState('');

	const { data, isLoading, isError } = useQuery({
		queryKey: ['portal-issue', issueId],
		queryFn: () => portalIssueService.getPortalIssue(token ?? '', issueId ?? 0),
		enabled: token != null && issueId != null,
		staleTime: 15_000,
	});

	const replyMutation = useMutation({
		mutationFn: (message: string) =>
			portalIssueService.replyToPortalIssue(token ?? '', issueId ?? 0, {
				message,
			}),
		onSuccess: (response) => {
			// The reply response carries the refreshed thread — swap it in and
			// invalidate the list so its message counts / status stay current.
			queryClient.setQueryData(['portal-issue', issueId], response.detail);
			void queryClient.invalidateQueries({ queryKey: ['portal-issues'] });
			setReply('');
		},
	});

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	const trimmedReply = reply.trim();
	const canSend =
		trimmedReply.length > 0 &&
		trimmedReply.length <= PORTAL_ISSUE_MESSAGE_MAX_LENGTH &&
		!replyMutation.isPending;

	const handleSend = (): void => {
		if (canSend) {
			replyMutation.mutate(trimmedReply);
		}
	};

	// The synthetic id-0 entry is the customer's original submission (it
	// lives on the issue, not in issue_note) — it renders inside the ticket
	// card; everything else is a response.
	const original = data?.thread.find((message) => message.id === 0) ?? null;
	const responses = data?.thread.filter((message) => message.id !== 0) ?? [];

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Support" backPath="/support" />
			<AccountBreadcrumb
				parent={{ label: 'Support', path: '/support' }}
				current={data != null ? `#${data.id}` : t('Request')}
			/>

			{/* Desktop way back to the list (PD-15002); mobile has the
			    MobilePageHeader arrow. */}
			<button
				type="button"
				onClick={() => {
					void navigate('/support');
				}}
				className="hidden md:inline-flex items-center gap-1.5 text-sm text-portal-accent hover:underline mb-3"
			>
				<FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
				{t('All Support Requests')}
			</button>

			{isLoading ? (
				<HistoryListSkeleton />
			) : isError || data == null ? (
				<EmptyState
					icon={faHeadset}
					title="Support request not found"
					hint="It may have been removed, or the link is incorrect."
				/>
			) : (
				<>
					{/* Ticket card: subject, reference, category, status, and the
					    original submission */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						<div className="flex items-start justify-between gap-3">
							<h1 className="text-lg md:text-xl font-bold text-default">
								{t(data.name)}
							</h1>
							<IssueStatusChip status={data.status} />
						</div>
						<div className="text-xs text-brand-muted mt-1">
							{t('#')}
							{data.id}
							{data.type != null && (
								<>
									{' · '}
									{t(data.type)}
								</>
							)}
							{' · '}
							{t('Submitted')}{' '}
							{formatDateTime(data.createdDate as unknown as string)}
						</div>
						{original != null && (
							<div className="mt-3 pt-3 border-t border-brand-border">
								<div className="text-sm text-default whitespace-pre-wrap break-words">
									{t(original.body)}
								</div>
							</div>
						)}
					</div>

					{/* Responses */}
					<h2 className="text-sm font-semibold text-default mb-2">
						{t('Responses')}
						{responses.length > 0 && (
							<span className="text-brand-muted font-normal">
								{' '}
								({responses.length})
							</span>
						)}
					</h2>
					<div className="flex flex-col gap-2 mb-4">
						{responses.map((message) => (
							<ResponseRow key={message.id} message={message} />
						))}
						{responses.length === 0 && (
							<p className="text-sm text-brand-muted">
								{t('No responses yet. Our team will follow up here.')}
							</p>
						)}
					</div>

					{/* Reply box */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-3">
						{(data.status as IssueStatus) === IssueStatus.DONE && (
							<p className="text-xs text-brand-muted mb-2">
								{t(
									'This request is resolved. Replying will reopen it for the support team.',
								)}
							</p>
						)}
						<textarea
							value={reply}
							onChange={(e) => {
								setReply(e.target.value);
							}}
							maxLength={PORTAL_ISSUE_MESSAGE_MAX_LENGTH}
							placeholder={t('Write a reply...')}
							disabled={replyMutation.isPending}
							rows={3}
							className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default resize-none"
						/>
						{replyMutation.isError && (
							<p className="text-xs text-danger mt-1">
								{t("We couldn't send your reply right now. Please try again.")}
							</p>
						)}
						<div className="flex justify-end mt-2">
							<button
								type="button"
								onClick={handleSend}
								disabled={!canSend}
								className="px-4 py-2 rounded-lg bg-portal-accent text-white text-sm font-medium hover:bg-portal-accent-hover disabled:opacity-50 flex items-center gap-2 min-h-touch"
							>
								<FontAwesomeIcon icon={faPaperPlane} />
								{replyMutation.isPending ? t('Sending...') : t('Send Reply')}
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
